<template>
  <div class="min-h-screen bg-gray-50 py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <div>
            <h1 class="text-xl sm:text-2xl font-bold text-gray-900">Leads Inbox</h1>
            <p class="text-sm sm:text-base text-gray-600 mt-1">{{ formTitle }}</p>
          </div>
          <div class="flex items-center gap-3 w-full sm:w-auto">
            <button
              @click="exportLeads"
              class="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              <icon name="download" class="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>

        <!-- Analytics -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 sm:mt-6">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border-2 border-blue-200 shadow-sm">
            <div class="flex items-center gap-2 mb-2">
              <icon name="aperture" class="w-5 h-5 text-blue-600" />
              <div class="text-sm font-medium text-gray-700">Total Views</div>
            </div>
            <div class="text-3xl font-bold text-blue-700">{{ analytics.views }}</div>
          </div>
          <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border-2 border-green-200 shadow-sm">
            <div class="flex items-center gap-2 mb-2">
              <icon name="contact" class="w-5 h-5 text-green-600" />
              <div class="text-sm font-medium text-gray-700">Total Submissions</div>
            </div>
            <div class="text-3xl font-bold text-green-700">{{ analytics.submissions }}</div>
          </div>
          <div class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border-2 border-purple-200 shadow-sm">
            <div class="flex items-center gap-2 mb-2">
              <icon name="status" class="w-5 h-5 text-purple-600" />
              <div class="text-sm font-medium text-gray-700">Conversion Rate</div>
            </div>
            <div class="text-3xl font-bold text-purple-700">{{ analytics.conversion_rate }}%</div>
          </div>
        </div>
      </div>

      <!-- Leads Table -->
      <div class="bg-white rounded-lg shadow-md overflow-hidden">
        <!-- Mobile Card View -->
        <div class="md:hidden divide-y divide-gray-200">
          <div v-for="lead in leads" :key="lead.id" class="p-4 hover:bg-gray-50">
            <div class="flex items-start justify-between mb-3">
              <div>
                <div class="text-sm font-medium text-gray-900">{{ formatDate(lead.captured_at) }}</div>
                <div class="text-xs text-gray-500 mt-1">Status: {{ lead.status }}</div>
              </div>
              <select
                :value="lead.status"
                @change="updateStatus(lead.id, $event.target.value)"
                class="text-xs rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
              >
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div class="space-y-2">
              <div v-for="field in fieldLabels" :key="field" class="text-sm">
                <span class="font-medium text-gray-500">{{ field }}:</span>
                <span class="ml-2 text-gray-900">{{ lead.data[field] || '-' }}</span>
              </div>
            </div>
            <button
              @click="viewLead(lead)"
              class="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View Details
            </button>
          </div>
          <div v-if="leads.length === 0" class="p-12 text-center">
            <icon name="contact" class="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p class="text-gray-500 font-medium">No leads captured yet</p>
            <p class="text-sm text-gray-400 mt-1">Leads will appear here once your form receives submissions</p>
          </div>
        </div>
        <!-- Desktop Table View -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date
                </th>
                <th
                  v-for="field in fieldLabels"
                  :key="field"
                  class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  {{ field }}
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr 
                v-for="lead in leads" 
                :key="lead.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ formatDate(lead.captured_at) }}
                </td>
                <td
                  v-for="field in fieldLabels"
                  :key="field"
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                >
                  {{ lead.data[field] || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <select
                    :value="lead.status"
                    @change="updateStatus(lead.id, $event.target.value)"
                    :class="[
                      'text-sm rounded-lg border-2 px-3 py-1.5 font-medium transition-all focus:outline-none focus:ring-2',
                      lead.status === 'new' ? 'border-blue-300 bg-blue-50 text-blue-700' :
                      lead.status === 'contacted' ? 'border-green-300 bg-green-50 text-green-700' :
                      'border-gray-300 bg-gray-50 text-gray-700'
                    ]"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="archived">Archived</option>
                  </select>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    @click="viewLead(lead)"
                    class="px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
              <tr v-if="leads.length === 0">
                <td :colspan="fieldLabels.length + 3" class="px-6 py-12 text-center">
                  <div class="flex flex-col items-center">
                    <icon name="contact" class="w-16 h-16 text-gray-300 mb-4" />
                    <p class="text-gray-500 font-medium">No leads captured yet</p>
                    <p class="text-sm text-gray-400 mt-1">Leads will appear here once your form receives submissions</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination && pagination.total > pagination.per_page" class="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
          <div class="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
            Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} results
          </div>
          <div class="flex gap-2 w-full sm:w-auto justify-center sm:justify-end">
            <button
              @click="loadPage(pagination.current_page - 1)"
              :disabled="pagination.current_page === 1"
              class="flex-1 sm:flex-none px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              @click="loadPage(pagination.current_page + 1)"
              :disabled="pagination.current_page === pagination.last_page"
              class="flex-1 sm:flex-none px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Lead Detail Modal -->
    <div
      v-if="selectedLead"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="selectedLead = null"
    >
      <div class="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-0 border border-gray-200">
        <div class="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <h3 class="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <icon name="contact" class="w-6 h-6 text-green-600" />
            Lead Details
          </h3>
          <button
            @click="selectedLead = null"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <icon name="close" class="w-5 h-5" />
          </button>
        </div>
        <div class="space-y-4">
          <div v-for="(value, key) in selectedLead.data" :key="key" class="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{{ key }}</div>
            <div class="text-base text-gray-900 font-medium">{{ value || '-' }}</div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div class="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Captured At</div>
              <div class="text-sm text-blue-900 font-medium">{{ formatDate(selectedLead.captured_at) }}</div>
            </div>
            <div class="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</div>
              <div class="text-sm text-gray-900 font-medium capitalize">{{ selectedLead.status }}</div>
            </div>
          </div>
          <div v-if="selectedLead.ip_address" class="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">IP Address</div>
            <div class="text-sm text-gray-900 font-mono">{{ selectedLead.ip_address }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon.vue'

export default {
  components: {
    Icon,
  },
  props: {
    formId: {
      type: Number,
      required: true,
    },
    formTitle: {
      type: String,
      default: 'Lead Form',
    },
  },
  data() {
    return {
      leads: [],
      pagination: null,
      selectedLead: null,
      fieldLabels: [],
      analytics: {
        views: 0,
        submissions: 0,
        conversion_rate: 0,
      },
    }
  },
  mounted() {
    this.loadLeads()
  },
  methods: {
    loadPage(page) {
      this.$inertia.visit(this.route('lead-forms.leads', this.formId), {
        data: { page },
        preserveState: true,
        preserveScroll: true,
      })
    },
    async updateStatus(leadId, status) {
      try {
        // This would require an update endpoint - for now just update locally
        const lead = this.leads.find(l => l.id === leadId)
        if (lead) {
          lead.status = status
        }
      } catch (error) {
        console.error('Error updating status:', error)
      }
    },
    viewLead(lead) {
      this.selectedLead = lead
    },
    formatDate(date) {
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async exportLeads() {
      try {
        const response = await this.axios.get(this.route('lead-forms.export', this.formId), {
          responseType: 'blob',
        })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `leads_${this.formId}_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error exporting leads:', error)
        let errorMessage = 'Failed to export leads'
        
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = 'No leads found to export.'
          } else if (error.response.status === 401 || error.response.status === 403) {
            errorMessage = 'You do not have permission to export leads.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          }
        }
        
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      }
    },
  },
}
</script>

